import { conceptIdLookupForNblyOrCorp, isValidCAPostalCode } from '../nbly-brand/helper_functions';
import { cachedAPIWrapper } from '../apiCaching/apiWrapper';
import { apiConfig } from '../config/apiConfig';
import { startLoader, stopLoader } from '../util/loader';
import { BrandResult } from './brand-result';
import { getPageSettings } from '../util/pageSettings';

export class SearchResultsPage {
    private static instance: SearchResultsPage;

    private searchResults : NodeListOf<HTMLElement> | null = null;
    private zipCodeEntryInBanner: HTMLInputElement | null = null;
    private serviceEntryInBanner: HTMLInputElement | null = null;
    private paramService: string | null = null;
    private paramName: string | null = null;
    private paramAddress: string | null = null;
    private popularServices: HTMLElement | null = null;
    private popularServicesList: NodeListOf<HTMLElement> | null = null;
    private moreServiceList: HTMLElement | null = null;
    private listenersAttached = false;
    private zipIcon: HTMLElement | null = null;
    private searchButton: HTMLElement | null = null;
    private readonly selectors = {
        results: '.nbly-search-results .container',
        zipInput: '.search-result-hero-banner .zip-input',
        popularGrid: '.popular-services-grid',
        popularServices: '.popular-service',
        serviceEntryInBanner: 'service-search-input',
        moreServiceList: '.services-list',
        zipIcon: '.zip-box .icon',
        searchButton: '.search-result-hero-banner .search-btn'
    } as const;

    public static getInstance(){
        this.instance ??= new SearchResultsPage();
        return this.instance;
    }

    private constructor() {
        this.domLookup();
    }

    domLookup(){
        this.searchResults = document.querySelectorAll<HTMLElement>(this.selectors.results);
        this.zipCodeEntryInBanner = document.querySelector(this.selectors.zipInput);
        this.serviceEntryInBanner = document.getElementById(this.selectors.serviceEntryInBanner) as HTMLInputElement | null;
        this.popularServices = document.querySelector(this.selectors.popularServices);
        this.moreServiceList = document.querySelector(this.selectors.moreServiceList);
        this.popularServicesList = document.querySelectorAll<HTMLElement>(this.selectors.popularGrid);
        this.zipIcon = document.querySelector(this.selectors.zipIcon);
        this.searchButton = document.querySelector(this.selectors.searchButton);
        this.attachClickEventListener();
        this.attachSearchButtonListener();
    }

    public init(){
        this.queryParamLookup();
    }

    queryParamLookup(){
        const urlParams = new URLSearchParams(window.location.search);
        this.paramService = urlParams.get("service") ?? '';
        this.paramName = urlParams.get("name") ?? '';
        this.paramAddress = urlParams.get("address") ?? '';
        if(this.paramAddress && this.paramAddress.length > 0){
            this.populateZipFromQueryParams();
        }else{
            this.hideSearchResults();
        }
        if(this.zipIcon){
            this.zipIcon.style.cursor = 'pointer';
        }

         const section = document.querySelector('.new-search-result-section') as HTMLElement;
        const newSearchResultsList = document.querySelectorAll('.new-search-results li');


        const parentContainer = document.querySelector('.nbly-search-results .container');

        if([this.paramService, this.paramName, this.paramAddress].every(v => typeof v ==='string' && v.trim().length > 0)){
            this.populateServiceFromQueryParams();
            this.showSearchResult();
            this.hideNoResultsContent();
            this.hideStaticContent();
        }else{
            this.hideSearchResults();
            parentContainer?.classList.add('hidden');
            const pageTemplate = getPageSettings('pagetemplate');
            if (pageTemplate?.toLowerCase().includes('opus 2 neighborly search results')){
                this.hideBottomServices();
            }
            if(section && newSearchResultsList?.length === 0){
                this.showNoResultsContent(this.paramName);
                this.showStaticContent();
            }
        }
    }

    private readonly onPopularServiceClick = (event: Event) => this.getClickedPopularServices(event);
    
    attachClickEventListener() {
        if (this.listenersAttached || !this.popularServicesList?.length || !this.serviceEntryInBanner) return;
        
        this.popularServicesList?.forEach(el => {
            if(el.classList.contains('service-redirection')) return;
            el.addEventListener('click', this.onPopularServiceClick);
        });
        this.listenersAttached = true;
    }

    getClickedPopularServices(event: Event){
        const target = event.target as HTMLElement;
        if (!target) return;
        const tile = target.closest('.popular-service-item');
        const raw = tile?.querySelector(".service-name")?.textContent ?? '';
        const clickedServiceName = raw.trim();
        if (!clickedServiceName) return;
        this.populateClickedTileInSearch(clickedServiceName);
    }

    populateClickedTileInSearch(clickedServiceName: string){
        if (!this.serviceEntryInBanner) return;
        this.serviceEntryInBanner.value = clickedServiceName;
        this.serviceEntryInBanner.focus();
        this.serviceEntryInBanner.dispatchEvent(new Event('input', { bubbles: true }));
        this.serviceEntryInBanner.dispatchEvent(new Event('change', { bubbles: true }));
        this.serviceEntryInBanner.dispatchEvent(new Event('keyup', { bubbles: true }));
    }

    populateZipFromQueryParams(){
        if(!this.zipCodeEntryInBanner){
            return;
        }
        this.zipCodeEntryInBanner.value = this.paramAddress ?? '';

    }

    populateServiceFromQueryParams(){
        if(!this.serviceEntryInBanner){
            return;
        }
        this.serviceEntryInBanner.value = this.paramName ?? '';
    }

    hideNoResultsContent(){
        const noResultContainer = document.querySelector('.no-search-results');
        if(noResultContainer){
            noResultContainer.classList.add('hidden');
        }
    }

    showNoResultsContent(serviceName: string | undefined) {
        const noResultContainer = document.querySelector('.no-search-results');
        if(noResultContainer){
            noResultContainer.classList.remove('hidden');
            const searchText = noResultContainer.querySelector('.nbly-search-title span') as HTMLSpanElement;
            if(searchText){
                searchText.textContent = `"${serviceName || ''}"`;
            }
        }
    }

    hideSearchResults(){
        if(!this.searchResults){
            return;
        }
        this.searchResults.forEach((searchResult) => {
            searchResult.classList.add("hidden");
        });
    }

    showSearchResult(){
        if(!this.searchResults){
            return;
        }
        this.searchResults.forEach((searchResult) => {
            searchResult.classList.remove("hidden");
        });
    }

    hideStaticContent(){
        if(!this.popularServices){
            return;
        }
        this.popularServices.classList.add("hidden");
        if(!this.moreServiceList){
            return;
        }
        this.moreServiceList.classList.add("hidden");
    }

    showStaticContent(){
        if(!this.popularServices){
            return;
        }
        this.popularServices.classList.remove("hidden");
        if(!this.moreServiceList){
            return;
        }
        this.moreServiceList.classList.remove("hidden");
    }

    hideBottomServices(){
        const popularServices = document.querySelector('.popular-service');
        if(popularServices){
            popularServices.classList.add('hidden');
        }
        const moreServiceList = document.querySelector('.services-list');
        if(moreServiceList){
            moreServiceList.classList.add('hidden');
        }
    }

    attachSearchButtonListener() {
            this.searchButton?.addEventListener('click', (e) => {
                this.handleSearchButtonClick(e);
            });
    }

    async handleSearchButtonClick(event: Event) {
        event.preventDefault();
        const serviceName = this.serviceEntryInBanner?.value?.trim() ?? '';
        const address = this.zipCodeEntryInBanner?.value?.trim() ?? '';
        const serviceId = this.serviceEntryInBanner?.dataset?.serviceId ?? '';
        const section = document.querySelector('.new-search-result-section') as HTMLElement;
        
        const nblySearchResults = document.querySelector('.nbly-search-results') as HTMLElement;

        const searchUrl = apiConfig.BRAND_SEARCH_API;
        

        // this.hideBottomServices();
        this.hideNoResultsContent();

        const response = await cachedAPIWrapper(`${searchUrl}/${serviceName}/${address}/${serviceId}`, 60);
        if (response && response?.Source === 'search-results') {
            const brandResult = BrandResult.getInstance();
            brandResult.updateMarkup(response);
            nblySearchResults.classList.remove('hidden');
            section.style.display = 'none';
            this.hideNoResultsContent();
        }
        else if (response && response?.Source === 'aggregate-results') {
            updateAggregateMarkup(response);
            section.style.display = 'block';
            this.hideNoResultsContent();
            // nblySearchResults.classList.add('hidden');
        }
        else if (response && response?.Source === 'no-results') {
            this.showNoResultsContent(serviceName);
            section.style.display = 'none';
            this.hideSearchResults();
            const brandResult = BrandResult.getInstance();
            brandResult.showBottomServices();
        }

    }
    

}

export function updateAggregateMarkup(data: any) {
        const section = document.querySelector('.new-search-result-section') as HTMLElement;
        if (!section) return;

        const markup = data.SearchServiceResultMarkup ?? '';
        const cleanedMarkup = markup.replace(/<!--[\s\S]*?-->/g, '').trim();

        if (cleanedMarkup) {
            section.innerHTML = markup;
            section.style.display = 'block';
            
            // Reinitialize pagination logic after updating markup
            pagination();
            
        }
}

function pagination(){

    const section = document.querySelector('.new-search-result-section') as HTMLElement;
    const items = document.querySelectorAll(".new-search-results li") as NodeListOf<HTMLLIElement>;
    const pageNumbers = document.querySelectorAll(".page-number") as NodeListOf<HTMLAnchorElement>;
    const resultText = document.querySelector(".pagination-results-text") as HTMLSpanElement;
    const paginationContainer = document.querySelector('.new-search-results-pagination.pagination') as HTMLElement;
    
    const itemsPerPage = 15;
    const totalResults = items.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);

        if (paginationContainer) {
    paginationContainer.style.display = totalPages <= 1 ? 'none' : 'block';
        }

    function showPage(page: number) {
        if (page < 1 || page > totalPages) return;

        const start = (page - 1) * itemsPerPage;
        const end = start + itemsPerPage;

        items.forEach((item, index) => {
            item.style.display = (index >= start && index < end)
                ? "list-item"
                : "none";
        });

        //  Fix display text edge case
        const displayStart = totalResults === 0 ? 0 : start + 1;
        const displayEnd = Math.min(end, totalResults);

        if(resultText){
            resultText.textContent = `Showing ${displayStart}-${displayEnd} of ${totalResults} results`;
        }
    }

    // Hide extra page buttons
    pageNumbers.forEach((btn) => {
        if(!paginationContainer)
            return;
        const page = parseInt(btn.textContent ?? '1');

        if (page > totalPages) {
            btn.parentElement?.style.setProperty("display", "none");
        }
    });

    //  Click handling
    pageNumbers.forEach((btn) => {
        if(!paginationContainer)
            return;
        btn.addEventListener("click", function (e) {
            e.preventDefault();

            const page = parseInt(this.textContent ?? '1');

            showPage(page);

            // update active class
            pageNumbers.forEach(b => b.classList.remove("current-page"));
            this.classList.add("current-page");
        });
    });
    
    const zipCodeEntryInBanner = document.querySelector('.search-result-hero-banner .zip-input')as HTMLInputElement;

    // Service list item click handler
    items.forEach((item) => {
        item.addEventListener('click', async function(e) {
            e.preventDefault();

            const urlParams = new URLSearchParams(window.location.search);
            const serviceName = urlParams.get('name') ?? '';
            const address = zipCodeEntryInBanner?.value?.trim()?? urlParams.get('address') ?? '';
            const serviceId = this.getAttribute('data-service-id');
            const dataServiceName = this.getAttribute('data-service-name');

            // Clear the search input field
            const searchInput = document.querySelector('.search-result-banner-wrap .service-input') as HTMLInputElement;
            if (searchInput) {
                searchInput.value = '';
            }

            startLoader();
            try {
                const searchEndpoint = isValidCAPostalCode(address) ? apiConfig.CA_BRAND_SEARCH_API : apiConfig.BRAND_SEARCH_API;
                const response = await cachedAPIWrapper(
                    `${searchEndpoint}/${dataServiceName ?? encodeURIComponent(serviceName)}/${encodeURIComponent(address)}/${encodeURIComponent(serviceId || '')}`,
                    60
                );
                
                
                if (response?.Source === 'search-results') {
                    const brandResult = BrandResult.getInstance();
                    brandResult.updateMarkup(response);
                    
                    // Update service-highlight with data-service-name AFTER updateMarkup
                    const serviceHighlight = document.querySelector('.service-highlight');
                    if (serviceHighlight && dataServiceName) {
                        serviceHighlight.textContent = dataServiceName;
                    }
                    
                    section.style.display = 'none';
                }
                else if(response?.Source === 'no-results'){
                    section.style.display = 'none';
                    const searchResultsPage = SearchResultsPage.getInstance();
                    searchResultsPage.hideSearchResults();
                    const brandResult = BrandResult.getInstance();
                    brandResult.showBottomServices();
                }
                stopLoader();
            } catch (error) {
                console.warn(`Error fetching service results: ${error}`);
                stopLoader();
            }
        });
    });

    showPage(1);

}

pagination();

document.addEventListener('DOMContentLoaded', () => {
    const shouldExecute = conceptIdLookupForNblyOrCorp();
    if(!shouldExecute)
        return
    const searchResultsPage = SearchResultsPage.getInstance();
    searchResultsPage.init();
});